// src/components/DistrictDashboard.jsx
import { useRef, useState, useEffect, useCallback } from "react";
import { File, FileArchive, FileImage, FileText, UploadCloud } from "lucide-react";
import DistrictSidebar from "./DistrictSidebar";
import api from "../services/api";
import toast from "react-hot-toast";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  Legend,
} from "recharts";

export default function DistrictDashboard() {
  const fileInput = useRef(null);

  const SECTOR_OPTIONS = [
    "Agriculture",
    "Education",
    "Energy",
    "Environment",
    "Governance",
    "Health",
    "ICT",
    "Social Protection",
    "Transport",
    "Water & Sanitation",
    "Other",
  ];

  const RISK_RATING_OPTIONS = [
    "Low",
    "Moderate",
    "Substantial",
    "High",
  ];

  const CYCLE_OPTIONS = Array.from({ length: 15 }, (_, index) => {
    const startYear = 2015 + index;
    return `${startYear}-${startYear + 1}`;
  });

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [formData, setFormData] = useState({
    district: "",
    projectName: "",
    coordinates: "",
    sector: "",
    riskRating: "",
    cycle: "",
    funding: "",
    subproject: "",
  });
  const [stats, setStats] = useState({
    submitted: 0,
    pending: 0,
    approved: 0,
    returned: 0,
    overdue: 0,
  });
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showUploadForm, setShowUploadForm] = useState(false);

  const token = localStorage.getItem("token");

  const greenToast = (message) =>
    toast(message, {
      style: {
        background: "#ecfdf5",
        color: "#065f46",
        borderLeft: "5px solid #16a34a",
      },
    });

  // Fetch dashboard stats (for cards)
  const fetchDashboardStats = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get("/district/dashboard-stats", {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStats(res.data.data);
    } catch (err) {
      console.error(err);
      toast.error("Failed to fetch dashboard stats");
    } finally {
      setLoading(false);
    }
  }, [token]);

  // Fetch chart data (line chart)
  const fetchChartData = useCallback(async () => {
    try {
      const res = await api.get("/district/my-esmps", {
        headers: { Authorization: `Bearer ${token}` },
      });

      const esmps = res.data.data || [];

      const districts = [
        "Balaka","Blantyre","Chikwawa","Chiradzulu","Chitipa",
        "Dedza","Dowa","Karonga","Kasungu","Likoma",
        "Lilongwe","Machinga","Mangochi","Mchinji","Mulanje",
        "Mwanza","Mzimba","Neno","Nkhata Bay","Nkhotakota",
        "Nsanje","Ntcheu","Ntchisi","Phalombe","Rumphi",
        "Salima","Thyolo","Zomba"
      ];

      const chartArr = districts
        .map(district => {
          const districtEsmp = esmps.filter(e => e.district === district);
          const submitted = districtEsmp.length;
          const overdue = districtEsmp.filter(e => e.overdue === true).length;
          return { district, Submitted: submitted, Overdue: overdue };
        })
        .filter(d => d.Submitted > 0 || d.Overdue > 0);

      setChartData(chartArr);
    } catch (err) {
      console.error("Failed to fetch chart data", err);
      toast.error("Failed to load chart data");
    }
  }, [token]);

  // Load stats and chart on component mount
  useEffect(() => {
    fetchDashboardStats();
    fetchChartData();
  }, [fetchDashboardStats, fetchChartData]);

  const blockedExtensions = new Set([
    ".app", ".bat", ".cmd", ".com", ".cpl", ".dll", ".dmg", ".exe",
    ".asp", ".aspx", ".cgi", ".gadget", ".hta", ".inf", ".ins", ".isp",
    ".jar", ".js", ".jse",
    ".lnk", ".msh", ".msi", ".msp", ".mst", ".pif", ".ps1", ".ps1xml",
    ".php", ".phtml", ".pl", ".ps2", ".ps2xml", ".psc1", ".psc2", ".py",
    ".rb", ".reg", ".scr", ".sh", ".sys",
    ".vb", ".vbe", ".vbs", ".ws", ".wsc", ".wsf", ".wsh",
  ]);

  const getExtension = (name) => {
    const dotIndex = name.lastIndexOf(".");
    return dotIndex >= 0 ? name.slice(dotIndex).toLowerCase() : "";
  };

  const fileKey = (file) => `${file.name.toLowerCase()}-${file.size}-${file.lastModified}`;

  const addFiles = (fileList) => {
    const files = Array.from(fileList || []);
    const safeFiles = files.filter((file) => !blockedExtensions.has(getExtension(file.name)));

    if (safeFiles.length !== files.length) {
      toast.error("Executable or script files are not allowed.");
    }

    setSelectedFiles((previousFiles) => {
      const existingKeys = new Set(previousFiles.map(fileKey));
      const uniqueFiles = safeFiles.filter((file) => {
        const key = fileKey(file);
        if (existingKeys.has(key)) return false;
        existingKeys.add(key);
        return true;
      });

      if (uniqueFiles.length !== safeFiles.length) {
        greenToast("Duplicate files were skipped.");
      }

      return [...previousFiles, ...uniqueFiles];
    });
  };

  const handleFileChange = (e) => {
    addFiles(e.target.files);
    e.target.value = "";
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return "0 B";
    const units = ["B", "KB", "MB", "GB"];
    const unitIndex = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
    return `${(bytes / 1024 ** unitIndex).toFixed(unitIndex === 0 ? 0 : 1)} ${units[unitIndex]}`;
  };

  const fileIcon = (file) => {
    const extension = getExtension(file.name);
    if ([".zip", ".rar", ".7z", ".tar", ".gz"].includes(extension)) return FileArchive;
    if (file.type.startsWith("image/")) return FileImage;
    if ([".pdf", ".doc", ".docx", ".txt", ".rtf"].includes(extension)) return FileText;
    return File;
  };

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleUpload = async () => {
    if (
      !formData.district ||
      !formData.projectName ||
      !formData.coordinates ||
      !formData.sector ||
      !formData.riskRating ||
      !formData.cycle ||
      !formData.funding
    ) {
      return greenToast("Please fill in all project details.");
    }

    if (selectedFiles.length === 0) {
      return greenToast("Please select at least one file.");
    }

    setLoading(true);
    setUploadProgress(0);
    const loadingToast = toast.loading("Uploading ESMP...", {
      style: { background: "#ecfdf5", color: "#065f46" },
    });

    try {
      for (const [fileIndex, file] of selectedFiles.entries()) {
        const formDataToSend = new FormData();
        formDataToSend.append("project_name", formData.projectName);
        formDataToSend.append("district", formData.district);
        formDataToSend.append("subproject", formData.subproject);
        formDataToSend.append("coordinates", formData.coordinates);
        formDataToSend.append("sector", formData.sector);
        formDataToSend.append("risk_rating", formData.riskRating);
        formDataToSend.append("cycle", formData.cycle);
        formDataToSend.append("funding_component", formData.funding);
        formDataToSend.append("esmp_file", file);

        await api.post("/district/submit", formDataToSend, {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
          onUploadProgress: (event) => {
            const fileProgress = event.total ? event.loaded / event.total : 0;
            setUploadProgress(Math.round(((fileIndex + fileProgress) / selectedFiles.length) * 100));
          },
        });
      }

      toast.success("ESMP uploaded successfully!", {
        id: loadingToast,
        style: {
          background: "#ecfdf5",
          color: "#065f46",
          borderLeft: "5px solid #16a34a",
        },
      });

      setFormData({
        district: "",
        projectName: "",
        coordinates: "",
        sector: "",
        riskRating: "",
        cycle: "",
        funding: "",
        subproject: "",
      });
      setSelectedFiles([]);
      setUploadProgress(0);
      setShowUploadForm(false);

      // Refresh stats and chart
      fetchDashboardStats();
      fetchChartData();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to upload ESMP.", {
        id: loadingToast,
      });
    } finally {
      setLoading(false);
    }
  };

  const statsDisplay = [
    { title: "Total ESMPs Submitted", value: stats.submitted },
    { title: "Pending Reviews", value: stats.pending },
    { title: "Approved ESMPs", value: stats.approved },
    { title: "Returned with Comments", value: stats.returned },
    { title: "Overdue ESMPs", value: stats.overdue, danger: true },
  ];

  return (
    <div className="flex h-screen overflow-hidden bg-gray-100">
      <DistrictSidebar />

      <main className="flex-1 overflow-y-auto p-6 space-y-6">
        {/* HEADER */}
        <div className="flex justify-between items-center bg-gradient-to-r from-green-700 to-green-500 text-white p-5 rounded-xl shadow-md">
          <h2 className="text-2xl font-bold">ESMP Writer Dashboard</h2>
          
        </div>

        {/* STATS CARDS */}
        <div className="bg-white p-6 rounded-xl shadow grid md:grid-cols-3 gap-4">
          {statsDisplay.map((item, i) => (
            <div
              key={i}
              className={`p-4 rounded border-l-4 ${
                item.danger ? "border-red-500 bg-red-50" : "border-green-700 bg-white"
              }`}
            >
              <p className="text-xs text-gray-500">{item.title}</p>
              <h2 className="text-3xl font-bold mt-2">{item.value}</h2>
            </div>
          ))}
        </div>

        {/* LINE CHART */}
        <div className="bg-white p-6 rounded-xl shadow max-w-5xl mx-auto">
          <h3 className="text-lg font-semibold text-green-700 mb-4">
            ESMP Submissions Overview
          </h3>
          {loading ? (
            <p className="text-gray-500 text-center py-6">Loading chart...</p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={chartData} margin={{ top: 20, right: 30, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="district" />
                <YAxis />
                <Tooltip />
                <Legend verticalAlign="top" height={36} />
                <Line type="monotone" dataKey="Submitted" stroke="#16a34a" strokeWidth={3} />
                <Line type="monotone" dataKey="Overdue" stroke="#ef4444" strokeWidth={3} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </div>

       {/* UPLOAD BUTTON & FORM */}
<div className="max-w-xl mx-auto space-y-4">
  {!showUploadForm && (
    <button
      onClick={() => setShowUploadForm(true)}
      className="w-full bg-green-700 hover:bg-green-800 text-white py-3 rounded-xl shadow"
    >
      Upload ESMP
    </button>
  )}

  {showUploadForm && (
    <div className="fixed inset-0 bg-black/50 z-50 flex justify-center items-start overflow-auto py-10 px-4">
      <div className="bg-white w-full max-w-3xl p-6 rounded-xl shadow-lg relative">
        <div className="flex justify-between items-center mb-6">
          <h3 className="text-xl font-semibold text-green-700">Upload ESMP</h3>
          <button
            onClick={() => setShowUploadForm(false)}
            className="text-red-600 text-sm font-semibold"
          >
            Close
          </button>
        </div>

        {/* FORM INPUTS WITH LABELS */}
        <div className="space-y-4">
          {Object.keys(formData).map((key) => (
            <div key={key} className="flex flex-col">
              <label className="text-sm font-semibold text-gray-700 mb-1">
                {key.replace(/([A-Z])/g, " $1").replace(/^./, str => str.toUpperCase())}
              </label>
              {key === "sector" ? (
                <select
                  name={key}
                  value={formData[key]}
                  onChange={handleInputChange}
                  className="w-full border p-2 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                  required
                >
                  <option value="">-- Select sector --</option>
                  {SECTOR_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
              ) : key === "riskRating" ? (
                <select
                  name={key}
                  value={formData[key]}
                  onChange={handleInputChange}
                  className="w-full border p-2 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                  required
                >
                  <option value="">-- Select risk rating --</option>
                  {RISK_RATING_OPTIONS.map((rating) => (
                    <option key={rating} value={rating}>
                      {rating}
                    </option>
                  ))}
                </select>
              ) : key === "cycle" ? (
                <select
                  name={key}
                  value={formData[key]}
                  onChange={handleInputChange}
                  className="w-full border p-2 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                  required
                >
                  <option value="">-- Select cycle --</option>
                  {CYCLE_OPTIONS.map((cycleOption) => (
                    <option key={cycleOption} value={cycleOption}>
                      {cycleOption}
                    </option>
                  ))}
                </select>
              ) : (
                <input
                  type="text"
                  name={key}
                  placeholder={key.replace(/([A-Z])/g, " $1")}
                  value={formData[key]}
                  onChange={handleInputChange}
                  className="w-full border p-2 rounded focus:outline-none focus:ring-2 focus:ring-green-500"
                />
              )}
            </div>
          ))}

          <label className="text-sm font-semibold text-gray-700 mb-1">ESMP Files</label>
          <input
            ref={fileInput}
            type="file"
            multiple
            hidden
            onChange={handleFileChange}
          />

          <div
            onClick={() => fileInput.current.click()}
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              addFiles(event.dataTransfer.files);
            }}
            className="border-2 border-dashed rounded-lg p-8 text-center cursor-pointer hover:border-green-700"
          >
            <UploadCloud className="mx-auto text-gray-400" size={42} />
            <p className="text-sm mt-2">Click to browse or drag and drop files</p>
            <p className="text-xs text-gray-500 mt-1">ZIP archives and other non-executable files are supported</p>
          </div>

          {loading && (
            <div className="space-y-1" aria-live="polite">
              <div className="flex justify-between text-xs text-gray-600">
                <span>Upload progress</span>
                <span>{uploadProgress}%</span>
              </div>
              <div className="h-2 overflow-hidden rounded bg-gray-200">
                <div className="h-full bg-green-600 transition-all" style={{ width: `${uploadProgress}%` }} />
              </div>
            </div>
          )}

          <button
            onClick={handleUpload}
            disabled={loading}
            className="w-full bg-green-700 hover:bg-green-800 text-white py-3 rounded mt-2"
          >
            {loading ? "Uploading..." : "Submit ESMP"}
          </button>

          {selectedFiles.length > 0 && (
            <div className="mt-4 space-y-2">
              <p className="text-sm font-semibold text-green-700">
                Selected files ({selectedFiles.length})
              </p>

              {selectedFiles.map((file, index) => {
                const FileIcon = fileIcon(file);
                return (
                <div
                  key={fileKey(file)}
                  className="flex justify-between items-center p-3 border rounded bg-green-50"
                >
                  <div className="flex min-w-0 items-center gap-3">
                    <FileIcon className="shrink-0 text-green-700" size={20} />
                    <div className="min-w-0">
                      <p className="truncate text-sm text-green-900">{file.name}</p>
                      <p className="text-xs text-gray-500">{formatFileSize(file.size)}</p>
                    </div>
                  </div>
                  <button
                    onClick={() =>
                      setSelectedFiles((prev) =>
                        prev.filter((_, i) => i !== index)
                      )
                    }
                    className="text-red-600 hover:text-red-800 text-sm font-semibold"
                  >
                    Remove
                  </button>
                </div>
              )})}
            </div>
          )}
        </div>
      </div>
    </div>
  )}
</div>
                
      </main>
    </div>
  );
}
